/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  knowledgeList: [],
  gsheetKBDetails: null,
  rows: [],
  activeFilters: [],
  allKnowledgeList: [],
  columns: {},
  embeddingConfig: null,
  isLoading: false,
  isError: false,
};

export const mutations = {
  setColumns(state, columns) { state.columns = columns; },
  setKnowledgeList(state, newList) { state.knowledgeList = newList; },
  setAllKnowledgeList(state, newList) { state.allKnowledgeList = newList; },
  setGSheetKBDetails(state, details) { state.gsheetKBDetails = details; },
  setRows(state, rows) { state.rows = rows; },
  addFilter(state, filter) {
    state.activeFilters = state.activeFilters.filter(
      f => !(f.type === filter.type && (!filter.column || f.column === filter.column))
    );
    state.activeFilters.push(filter);
  },
  removeFilter(state, index) { state.activeFilters.splice(index, 1); },
  clearFilters(state) { state.activeFilters = []; },
  SET_LOADING(state, value) { state.isLoading = value; },
  SET_ERROR(state, value) { state.isError = value; },
  SET_EMBEDDING_CONFIG(state, config) { state.embeddingConfig = config; },
}

export const actions = {
  async createKnowledge({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases`),
        payload
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchGSheetKBList({ rootGetters, commit }, { page = 1, pageSize = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases?page=${page}&limit=${pageSize}`)
      );
      commit('setKnowledgeList', res.data.items);
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchAllKBs({ dispatch, commit }) {
    let page = 1;
    let pageSize = 100;
    let all = [];
    let hasMore = true;

    while (hasMore) {
      const res = await dispatch('fetchGSheetKBList', { page, pageSize });
      if (res && res.items) {
        all = [...all, ...res.items];
        hasMore = res.items.length === pageSize;
        page++;
      } else {
        hasMore = false;
      }
    }

    commit('setAllKnowledgeList', all);
    return all;
  },

  async editKnowledge({ rootGetters }, { knowledgeId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}`),
        payload
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async editKnowledgeDescription({ rootGetters }, { knowledgeId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/description`),
        payload
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async deleteKnowledge({ rootGetters }, knowledgeId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}`)
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchGSheetKB({ rootGetters, commit }, knowledgeId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}`)
      );
      commit('setGSheetKBDetails', res.data);
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchRows(
    { rootGetters, commit },
    { knowledgeId, page = 1, limit = 20, enabled = null, search_options = null, q = null, sort_by = null, sort_order = 'asc' }
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const params = { page, limit };
      if (enabled !== null) params.enabled = enabled;
      if (search_options) params.search_options = search_options;
      if (q) params.q = q;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      const res = await axios.get(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/rows`),
        { params }
      );

      commit('setRows', res.data);
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async syncGSheetKB({ rootGetters }, knowledgeId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/sync`),
        {}
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async toggleGSheetRow({ rootGetters }, rowId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/gsheet-kb/gsheet-rows/${rowId}/toggle`),
        {}
      );
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchColumns({ rootGetters, commit }, knowledgeId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/columns`)
      );
      commit('setColumns', res.data);
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async editColumnDescription({ rootGetters }, { knowledgeId, columnName, description }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/columns/${columnName}/description`),
        { description }
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async editColumnAllowedValues({ rootGetters }, { knowledgeId, columnName, allowed_values }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/columns/${columnName}/allowed-values`),
        { allowed_values: [...allowed_values] }
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async editColumnIsEnabled({ rootGetters }, { knowledgeId, columnName, is_enabled }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${knowledgeId}/columns/${columnName}/enabled`),
        { is_enabled }
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async getEmbeddingConfig({ commit, rootGetters }, { kb_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('SET_LOADING', true);
    commit('SET_ERROR', false);

    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${kb_id}/embedding-config`)
      );
      commit('SET_EMBEDDING_CONFIG', res.data);
      return res.data;
    } catch (e) {
      commit('SET_ERROR', true);
      throw e;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateEmbeddingConfig({ commit, rootGetters }, { kb_id, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('SET_LOADING', true);
    commit('SET_ERROR', false);

    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/gsheet-kb/accounts/${accountId}/gsheet-knowledge-bases/${kb_id}/embedding-config`),
        payload
      );
      commit('SET_EMBEDDING_CONFIG', res.data);
      return res.data;
    } catch (e) {
      commit('SET_ERROR', true);
      throw e;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async regenerateEmbeddings({ rootGetters }, { kb_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/gsheet-kb/${kb_id}/regenerate-embeddings`),
        null,
        { params: { account_id: accountId } }
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  },
}

export const getters = {
  getActiveFilters: state => state.activeFilters,
  getGSheetKBList: (state) => state.allKnowledgeList,
  embeddingConfig: state => state.embeddingConfig,
  isLoading: state => state.isLoading,
  isError: state => state.isError,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
